import { Children, type FC, type PropsWithChildren, useEffect, useRef, useState } from "react";

import ChevronLeft from "@/design-system/icons/chevron-left";
import ChevronRight from "@/design-system/icons/chevron-right";
import * as stylex from "@stylexjs/stylex";

import { type PropsWithStyleX, type QueryXsMax, colors } from "./../../styles/tokens.stylex";

export interface ICarousel extends PropsWithChildren<PropsWithStyleX> {
    arrowStyleX?: stylex.StyleXStyles;
    carouselContainerStyleX?: stylex.StyleXStyles;
    itemStyleX?: stylex.StyleXStyles;
    itemsToShow?: number;
    centerMode?: boolean;
    fullWidth?: boolean;
    showArrows?: boolean;
    customGap?: string;
    onMobileSlide?: () => void;
    onPrevClick?: () => void;
    onNextClick?: () => void;
}

export type ForwardRefImperativeHandleReturn = HTMLOListElement;

const Carousel: FC<ICarousel> = (props) => {
    const [disabledArrowState, setDisabledArrowsState] = useState({
        left: true,
        right: false,
    });

    const carouselRef = useRef<HTMLOListElement>(null);

    const updateScroll = (scrollPosition?: number) => {
        if (carouselRef.current === null) {
            return;
        }

        const { scrollWidth, clientWidth } = carouselRef.current;
        const maxScrollPosition = Math.max(0, scrollWidth - clientWidth);

        if (scrollPosition === undefined) {
            scrollPosition = carouselRef.current.scrollLeft;
        } else {
            scrollPosition = Math.min(maxScrollPosition, Math.max(0, scrollPosition));
            carouselRef.current.scrollTo({
                left: scrollPosition,
                behavior: "smooth",
            });
        }

        const boundaryTolerance = 1;

        const disableLeft = scrollPosition <= boundaryTolerance;
        const disableRight = maxScrollPosition - scrollPosition <= boundaryTolerance;

        setDisabledArrowsState((currentState) =>
            disableLeft === currentState.left && disableRight === currentState.right
                ? currentState
                : { right: disableRight, left: disableLeft },
        );
    };

    const onSlide = () => {
        updateScroll();

        if (props.onMobileSlide) {
            props.onMobileSlide();
        }
    };

    const liProps = stylex.props(styles.item, props.centerMode && styles.itemCenter, props.itemStyleX);

    const children = Children.map(props.children, (c) => (
        <li {...liProps} role="listitem" tabIndex={0}>
            {c}
        </li>
    ));

    const carousel = (
        <ol
            {...stylex.props(
                styles.carousel(props.itemsToShow || 1, props.customGap || "20px"),
                props.fullWidth && styles.fullWidth,
                props.centerMode && styles.itemCenter,
                props.styleX,
            )}
            ref={carouselRef}
            onScroll={() => updateScroll()}
            onTouchEnd={onSlide}
            role="list"
        >
            {children}
        </ol>
    );

    useEffect(updateScroll, [children?.length]);

    const baseOnNextClick = () => {
        const newScrollPosition = carouselRef.current!.scrollLeft + carouselRef.current!.clientWidth;
        updateScroll(newScrollPosition);

        if (props.onNextClick) {
            props.onNextClick();
        }
    };

    const baseOnPrevClick = () => {
        const newScrollPosition = carouselRef.current!.scrollLeft - carouselRef.current!.clientWidth;
        updateScroll(newScrollPosition);

        if (props.onPrevClick) {
            props.onPrevClick();
        }
    };

    if (props.showArrows) {
        return (
            <div {...stylex.props(styles.arrowContainer, props.carouselContainerStyleX)}>
                <button
                    {...stylex.props(styles.arrow, styles.arrowPrev, props.arrowStyleX)}
                    type="button"
                    disabled={disabledArrowState.left}
                    onClick={baseOnPrevClick}
                    aria-label="scroll-left"
                >
                    <ChevronLeft />
                </button>
                {carousel}
                <button
                    {...stylex.props(styles.arrow, styles.arrowNext, props.arrowStyleX)}
                    type="button"
                    disabled={disabledArrowState.right}
                    onClick={baseOnNextClick}
                    aria-label="scroll-right"
                >
                    <ChevronRight />
                </button>
            </div>
        );
    }

    return carousel;
};

const xsMax: QueryXsMax = "@media (max-width: 759px)";

const arrowOffset = "60px";

const styles = stylex.create({
    carousel: (itemsToShow: number, gridGap: string) => ({
        margin: "0",
        gap: gridGap,
        display: "grid",
        gridAutoColumns: `calc(${100 / itemsToShow}% - (${gridGap} - 1px))`,
        gridAutoFlow: "column",
        gridTemplateRows: "100%",
        position: "relative",
        scrollBehavior: "unset",
        scrollbarWidth: "none",

        willChange: "scroll-position",
        height: "100%",
        overflowX: "scroll",
        overflowY: "hidden",
        width: "100%",

        "::-webkit-scrollbar": {
            display: "none",
        },
    }),

    item: {
        scrollSnapAlign: { default: "start", [xsMax]: "center" },
    },
    itemCenter: {
        scrollSnapAlign: "center",
    },
    fullWidth: {
        margin: {
            default: null,
            [xsMax]: "0 -10px",
        },
        padding: {
            default: null,
            [xsMax]: "0 10px",
        },
        width: null,
    },
    arrow: {
        strokeWidth: 2,

        transition: "all 300ms",
        color: colors.fg_Primary,
        opacity: {
            default: 0.5,
            ":hover": 1,
        },
        position: "absolute",
        transform: "translateY(-50%)",
        visibility: {
            default: "visible",
            ":disabled": "hidden",
        },
        zIndex: 1,
        height: 28,
        top: "50%",
        width: 28,
    },
    arrowPrev: {
        left: "0",
    },
    arrowNext: {
        right: "0",
    },
    arrowContainer: {
        padding: `0 ${arrowOffset}`,
        position: "relative",
        height: "100%",
    },
});

export default Carousel;
